import { findTable } from '../../config/dataTables.registry';

interface MaintenanceMapping {
  /** `key` of the entry in dataTables.registry. */
  table: string;
  /**
   * Order field supplying the `?q=` term, when it isn't the mapped field
   * itself — e.g. `carrierCode` holds a recno, so the trucker name in
   * `carrier` is what's worth searching for.
   */
  queryField?: string;
}

/**
 * camelCase order field key -> the data-management table that maintains it.
 * Only fields whose value is a code or name with a row in that table belong
 * here; the route and label are resolved from the registry so a route rename
 * can't rot this map. New entries must point at a table whose route is live
 * (`isTableReady`) — DashboardLayout only registers those, so an unwired key
 * would open a 404 tab.
 */
const MAINTENANCE_FIELDS: Record<string, MaintenanceMapping> = {
  carrierCode: { table: 'carrierprofile', queryField: 'carrier' },
  scac: { table: 'scacprofiles' },
  chassisProvider: { table: 'chassisprovider' },
  equipType: { table: 'equipmenttype' },
  portOfOriginCode: { table: 'ports' },
  portOfDischargeCode: { table: 'ports' },
  rail: { table: 'rails' },
};

export interface MaintenanceTarget {
  /** Data-management route to open, e.g. `/data-management/scacs`. */
  route: string;
  /** Table name, for the menu item's tooltip. */
  label: string;
  /** Order field key whose value seeds the table search. */
  queryField: string;
}

/**
 * First of `fieldKeys` that has a maintenance table, so a compound label
 * (`Size / Type`) resolves on whichever half is maintained. Unknown fields —
 * and tables missing from the registry — yield undefined, hiding the item.
 */
export function findMaintenanceTarget(fieldKeys: string[]): MaintenanceTarget | undefined {
  for (const fieldKey of fieldKeys) {
    const mapping = MAINTENANCE_FIELDS[fieldKey];
    if (!mapping) continue;
    const table = findTable(mapping.table);
    if (!table) continue;
    return {
      route: table.route,
      label: table.label,
      queryField: mapping.queryField ?? fieldKey,
    };
  }
  return undefined;
}

/** Deep-links the table's search box (TablemaintPage reads `?q=`). */
export function maintenanceUrl(route: string, term: string): string {
  return term ? `${route}?q=${encodeURIComponent(term)}` : route;
}
